import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Zap, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import ContactModal from './ContactModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SCOPES = [
  { id: 'weekend', label: 'Ce week-end', icon: '🏖️' },
  { id: 'week', label: 'Cette semaine', icon: '📅' },
  { id: 'today', label: "Aujourd'hui", icon: '⚡' },
  { id: 'tomorrow', label: 'Demain', icon: '🌅' },
];

const STATUS_CONFIG = {
  disponible: { label: 'Disponible', color: '#22C55E', bg: 'rgba(34,197,94,0.12)', icon: CheckCircle },
  en_demande: { label: 'En demande', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', icon: AlertCircle },
  controle_requis: { label: 'Sous contrôle', color: '#F97316', bg: 'rgba(249,115,22,0.1)', icon: AlertCircle },
};

function ProductDispoCard({ product, onContact }) {
  const statusConf = STATUS_CONFIG[product.statut_dispo] || STATUS_CONFIG.disponible;
  const StatusIcon = statusConf.icon;

  return (
    <div className="card-premium overflow-hidden group"
      style={{ background: '#FFFFFF' }}>
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: '160px' }}>
        {product.image ? (
          <img src={product.image} alt={product.nom}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl"
            style={{ background: 'rgba(212,175,55,0.08)' }}>🎪</div>
        )}
        {/* Badge dispo */}
        <div className="absolute top-2 left-2">
          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ background: statusConf.bg, color: statusConf.color, backdropFilter: 'blur(8px)', border: `1px solid ${statusConf.color}30` }}>
            <StatusIcon size={11} />
            {statusConf.label}
          </span>
        </div>
        {product.featured && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-bold"
              style={{ background: '#D4AF37', color: '#0B1E20' }}>⭐ Top</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{product.categorie}</p>
        <h3 className="font-bold text-sm line-clamp-2 mb-2" style={{ color: 'var(--ink)' }}>{product.nom}</h3>

        {product.statut_dispo === 'en_demande' && (
          <p className="text-xs mb-2 p-2 rounded-lg"
            style={{ background: 'rgba(245,158,11,0.08)', color: '#C58B19', lineHeight: '1.4' }}>
            Ce produit est en cours de demande. Disponibilité confirmée après contrôle Show Événement.
          </p>
        )}

        <div className="flex items-center justify-between mt-2">
          <span className="font-bold text-sm" style={{ color: '#D4AF37' }}>
            à partir de {product.tarif}€
          </span>
          <div className="flex gap-1.5">
            <Link to={`/produit/${product.id}`}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
              Voir
            </Link>
            <button
              onClick={() => onContact(product.nom)}
              className="px-2.5 py-1.5 rounded-lg text-xs font-bold"
              style={{ background: '#D4AF37', color: '#0B1E20', border: 'none', cursor: 'pointer' }}>
              Devis
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DispoUrgenceBlock() {
  const [activeScope, setActiveScope] = useState('weekend');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSujet, setContactSujet] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/disponibilites/rapide?scope=${activeScope}`);
      const json = await res.json();
      setData(json);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeScope]);

  useEffect(() => { load(); }, [load]);

  const handleContact = (productNom) => {
    setContactSujet(`Demande de devis — ${productNom}`);
    setContactOpen(true);
  };

  return (
    <section className="section-py" style={{ background: 'linear-gradient(135deg, #0B1E20 0%, #1a3a3c 100%)' }}>
      <div className="container-ose">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Zap size={18} style={{ color: '#D4AF37' }} />
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#D4AF37' }}>
                Disponibilités en temps réel
              </span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold"
              style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
              {data?.label || 'Disponible ce week-end'}
            </h2>
            {data?.date_start && (
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <Clock size={12} className="inline mr-1" />
                Vérification en direct — {data.total} produit{data.total > 1 ? 's' : ''} disponible{data.total > 1 ? 's' : ''}
              </p>
            )}
          </div>
          <Link to="/catalogue"
            className="flex items-center gap-1 text-sm font-semibold"
            style={{ color: '#D4AF37' }}>
            Voir tout le catalogue <ArrowRight size={14} />
          </Link>
        </div>

        {/* Filtres scopes */}
        <div className="flex gap-2 overflow-x-auto mb-6 pb-1">
          {SCOPES.map(s => (
            <button key={s.id} onClick={() => setActiveScope(s.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold flex-shrink-0 transition-all"
              style={{
                background: activeScope === s.id ? '#D4AF37' : 'rgba(255,255,255,0.08)',
                color: activeScope === s.id ? '#0B1E20' : 'rgba(255,255,255,0.7)',
                border: activeScope === s.id ? 'none' : '1px solid rgba(255,255,255,0.12)',
                cursor: 'pointer',
              }}>
              {s.icon} {s.label}
            </button>
          ))}
        </div>

        {/* Légende */}
        <div className="flex flex-wrap gap-4 mb-5">
          {Object.entries(STATUS_CONFIG).map(([k, v]) => {
            const Icon = v.icon;
            return (
              <div key={k} className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                <Icon size={12} style={{ color: v.color }} />
                <span style={{ color: v.color }}>{v.label}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            🔴 = Réservé / Indisponible (non affiché)
          </div>
        </div>

        {/* Grille produits */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-64 rounded-2xl animate-pulse"
                style={{ background: 'rgba(255,255,255,0.05)' }} />
            ))}
          </div>
        ) : !data || data.total === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Tous les produits sont réservés pour cette période !
            </p>
            <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Choisissez une autre période ou contactez-nous pour vérifier les disponibilités.
            </p>
            <button
              onClick={() => handleContact('Demande disponibilité')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
              style={{ background: '#D4AF37', color: '#0B1E20', border: 'none', cursor: 'pointer' }}>
              Vérifier les disponibilités
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.produits.slice(0, 8).map(p => (
              <ProductDispoCard key={p.id} product={p} onContact={handleContact} />
            ))}
          </div>
        )}

        {/* CTA bas */}
        <div className="mt-6 p-4 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
          <div className="flex items-center gap-3">
            <Calendar size={18} style={{ color: '#D4AF37' }} />
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
              Disponibilité finale confirmée après validation Show Événement
            </p>
          </div>
          <Link to="/creer-evenement"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold flex-shrink-0"
            style={{ background: '#D4AF37', color: '#0B1E20' }}>
            Créer mon devis <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        sujetPrefill={contactSujet}
        sourcePage="Bloc disponibilités homepage"
      />
    </section>
  );
}
